<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="isOpen && claim"
        class="fixed inset-0 z-50 overflow-y-auto"
        @click.self="$emit('close')"
      >
        <!-- Backdrop -->
        <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

        <!-- Modal -->
        <div class="flex min-h-full items-center justify-center p-4">
          <div class="relative border border-gray-200 dark:border-gray-700 bg-white dark:bg-black rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <!-- Header -->
            <div class="sticky top-0 bg-white dark:bg-black border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
              <div class="flex items-center">
                <div class="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center mr-3">
                  <ViewIcon classes="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
                    Claim Details
                  </h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    {{ claim.claim_number }}
                  </p>
                </div>
              </div>
              <button
                @click="$emit('close')"
                class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors duration-200"
              >
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Content -->
            <div class="px-6 py-6">
              <!-- Claim Status -->
              <div class="mb-6">
                <label class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2 block">
                  Claim Status
                </label>
                <span
                  :class="getStatusClass(claim.claim_status)"
                  class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium"
                >
                  <span
                    class="w-2 h-2 rounded-full mr-2"
                    :class="getStatusDotClass(claim.claim_status)"
                  ></span>
                  {{ claim.claim_status }}
                </span>
              </div>

              <!-- Rejection Reason (if rejected) -->
              <div v-if="claim.claim_status === 'rejected' && claim.rejection_reason" class="mb-6">
                <label class="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2 block">
                  Rejection Reason
                </label>
                <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-2">
                  <p class="text-sm text-red-800 dark:text-red-300">{{ claim.rejection_reason }}</p>
                </div>
              </div>

              <!-- Customer Information -->
              <div class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                  <svg class="w-5 h-5 mr-2" style="color: var(--brand-accent);" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
                  </svg>
                  Customer Information
                </h3>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Customer Name</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">
                      {{ claim.user?.first_name }} {{ claim.user?.last_name }}
                    </p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Email</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium break-all">
                      {{ claim.user?.email || 'N/A' }}
                    </p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Mobile Number</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">
                      {{ claim.user?.user_detail?.mobile || claim.user?.userDetail?.mobile || 'N/A' }}
                    </p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Brand</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">
                      {{ claim.brand?.brand_name || 'N/A' }}
                    </p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Date Submitted</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">
                      {{ formatDate(claim.created_at) }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Vehicle Information -->
              <div class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                  <svg class="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/>
                    <circle cx="7" cy="17" r="2"/>
                    <path d="M9 17h6"/>
                    <circle cx="17" cy="17" r="2"/>
                  </svg>
                  Vehicle Information
                </h3>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Year</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">
                      {{ claim.vehicle?.vehicle_year || 'N/A' }}
                    </p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Make</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">
                      {{ claim.vehicle?.vehicle_make || 'N/A' }}
                    </p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Model</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">
                      {{ claim.vehicle?.vehicle_model || 'N/A' }}
                    </p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Plate Number</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">
                      {{ claim.vehicle?.plate_number || 'N/A' }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Video Call Schedule -->
              <div v-if="claim.videocall_schedule" class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                  <svg class="w-5 h-5 mr-2" style="color: var(--brand-accent);" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  Video Call Schedule
                </h3>
                <div class="grid grid-cols-1 gap-4">
                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Scheduled Date & Time</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ formatDate(claim.videocall_schedule) }}</p>
                  </div>
                </div>
              </div>

              <!-- Tire Information -->
              <div class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                  <svg class="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="9"/>
                    <circle cx="12" cy="12" r="5"/>
                    <path d="M12 3v2M12 19v2M3 12h2M19 12h2"/>
                  </svg>
                  Tire Information
                </h3>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Pattern</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">
                      {{ claim.tire?.pattern || 'N/A' }}
                    </p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Size</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">
                      {{ claim.tire?.tire_size || 'N/A' }}
                    </p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">DOT</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">
                      {{ claim.tire?.dot || 'N/A' }}
                    </p>
                  </div>

                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Position</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">
                      {{ claim.tire?.tire_position || 'N/A' }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Replacement Tire Dealer -->
              <div v-if="claim.dealer" class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                  <svg class="w-5 h-5 mr-2" style="color: var(--brand-accent);" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                  Replacement Tire Dealer
                </h3>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Dealer Name</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ claim.dealer.dealer_name || 'N/A' }}</p>
                  </div>
                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Address</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ claim.dealer.dealer_address || 'N/A' }}</p>
                  </div>
                  <div class="bg-gray-50 dark:bg-black rounded-lg border border-gray-100 dark:border-gray-800 p-2">
                    <label class="text-xs font-medium uppercase tracking-wide" style="color: var(--brand-primary);">Contact</label>
                    <p class="mt-1 text-gray-900 dark:text-white font-medium">{{ claim.dealer.dealer_contact || 'N/A' }}</p>
                  </div>
                </div>
              </div>

              <!-- Damage Photos Gallery -->
              <div v-if="claim.claim_photos && claim.claim_photos.length > 0" class="mb-6">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white flex items-center mb-4">
                  <svg class="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="9" cy="9" r="2"/>
                    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
                  </svg>
                  Photos & Video ({{ claim.claim_photos.length }})
                </h3>

                <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  <div
                    v-for="(photo, index) in claim.claim_photos"
                    :key="photo.claim_photo_id"
                    class="relative group cursor-pointer"
                    @click="handleMediaClick(photo, index)"
                  >
                    <!-- Image Preview -->
                    <div v-if="!isVideo(photo)" class="relative">
                      <img
                        :src="getPhotoUrl(photo.photo_path)"
                        :alt="getMediaLabel(photo.media_type)"
                        class="w-full h-32 object-cover rounded-lg border-2 border-gray-200 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-200"
                      />
                      <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-200 rounded-lg flex items-center justify-center">
                        <svg class="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                    </div>

                    <!-- Video Preview -->
                    <div v-else class="relative">
                      <div class="w-full h-32 bg-gray-200 dark:bg-gray-900 rounded-lg border-2 border-gray-200 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-200 flex items-center justify-center">
                        <svg class="w-12 h-12 text-gray-500 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-200 rounded-lg"></div>
                    </div>

                    <!-- Media Type Label -->
                    <div class="absolute top-2 left-2 px-2 py-1 bg-black bg-opacity-70 rounded text-xs font-medium text-white">
                      {{ getMediaLabel(photo.media_type) }}
                    </div>

                    <!-- File Size (if available) -->
                    <div v-if="photo.file_size" class="absolute bottom-2 right-2 px-2 py-1 bg-black bg-opacity-70 rounded text-xs text-white">
                      {{ formatFileSize(photo.file_size) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Footer Actions -->
            <div class="sticky bottom-0 bg-gray-50 dark:bg-black border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-end space-x-3">
              <button
                @click="$emit('close')"
                class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
              >
                Close
              </button>
              <button
                @click="$emit('edit', claim)"
                class="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <EditIcon classes="h-5 w-5" />
                <span>Edit Claim</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- Lightbox for full-size image viewing -->
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="lightboxOpen"
        class="fixed inset-0 z-[60] bg-black bg-opacity-90 flex items-center justify-center p-4"
        @click="closeLightbox"
      >
        <button
          @click="closeLightbox"
          class="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors duration-200 z-10"
        >
          <svg class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <button
          v-if="currentPhotoIndex > 0"
          @click.stop="previousPhoto"
          class="absolute left-4 text-white hover:text-gray-300 transition-colors duration-200"
        >
          <svg class="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <img
          v-if="claim?.claim_photos && claim.claim_photos[currentPhotoIndex]"
          :src="getPhotoUrl(claim.claim_photos[currentPhotoIndex].photo_path)"
          :alt="`Damage photo ${currentPhotoIndex + 1}`"
          class="max-w-full max-h-full object-contain"
          @click.stop
        />

        <button
          v-if="claim?.claim_photos && currentPhotoIndex < claim.claim_photos.length - 1"
          @click.stop="nextPhoto"
          class="absolute right-4 text-white hover:text-gray-300 transition-colors duration-200"
        >
          <svg class="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <div class="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm">
          {{ currentPhotoIndex + 1 }} / {{ claim?.claim_photos?.length || 0 }}
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { Claim, ClaimPhoto } from '@/types/claim';
import ViewIcon from '@/components/icons/ViewIcon.vue';
import EditIcon from '@/components/icons/EditIcon.vue';

const props = defineProps<{
  isOpen: boolean;
  claim: Claim | null;
}>();

defineEmits<{
  close: [];
  edit: [claim: Claim];
}>();

// Lightbox state
const lightboxOpen = ref(false);
const currentPhotoIndex = ref(0);

// function getPhotoUrl(path: string): string {
//   const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000';
//   // Remove /api or /api/public from the URL to get the base URL
//   const baseUrl = apiUrl.replace(/\/api(\/public)?$/, '');
//   return `${baseUrl}/storage/${path}`;
// }

function getPhotoUrl(path: string): string {
  const baseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000';
  // const baseUrl = process.env.VUE_APP_API_URL?.replace('/api', '') || 'http://localhost:8000';
  return `${baseUrl}/storage/${path}`;
}

function openLightbox(index: number) {
  currentPhotoIndex.value = index;
  lightboxOpen.value = true;
}

function closeLightbox() {
  lightboxOpen.value = false;
}

function previousPhoto() {
  if (currentPhotoIndex.value > 0) {
    currentPhotoIndex.value--;
  }
}

function nextPhoto() {
  if (props.claim?.claim_photos && currentPhotoIndex.value < props.claim.claim_photos.length - 1) {
    currentPhotoIndex.value++;
  }
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'under review':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'approved':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'rejected':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    case 'ready for pick-up':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    case 'fulfilled':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
  }
}

function getStatusDotClass(status: string): string {
  switch (status) {
    case 'under review':
      return 'bg-yellow-500 dark:bg-yellow-400';
    case 'approved':
      return 'bg-green-500 dark:bg-green-400';
    case 'rejected':
      return 'bg-red-500 dark:bg-red-400';
    case 'ready for pick-up':
      return 'bg-blue-500 dark:bg-blue-400';
    case 'fulfilled':
      return 'bg-purple-500 dark:bg-purple-400';
    default:
      return 'bg-gray-500 dark:bg-gray-400';
  }
}

// Check if media is a video
function isVideo(photo: ClaimPhoto): boolean {
  return photo.media_type === 'damage_video';
}

// Get human-readable label for media type
function getMediaLabel(mediaType?: string): string {
  switch (mediaType) {
    case 'whole_tire':
      return 'Whole Tire';
    case 'tread_view':
      return 'Tread View';
    case 'damage_video':
      return 'Damage Video';
    default:
      return 'Media';
  }
}

// Format file size from bytes to human-readable format
function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}

// Handle media click (lightbox for photos, video playback for videos)
function handleMediaClick(photo: ClaimPhoto, index: number) {
  if (isVideo(photo)) {
    // Open video in new tab for playback
    const videoUrl = getPhotoUrl(photo.photo_path);
    window.open(videoUrl, '_blank');
  } else {
    // Open lightbox for photos
    openLightbox(index);
  }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
